"use client";

import {
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslations } from "next-intl";
import { FC } from "react";

import RichText from "@/components/rich_text";
import { ImpactDirectionCategory } from "@/types/comment";
import cn from "@/utils/core/cn";

type Props = {
  impact: ImpactDirectionCategory;
  className?: string;
  option?: string;
  unit?: string;
  isCompact?: boolean;
  hideIcon?: boolean;
};

export const KeyFactorImpactDirectionLabel: FC<Props> = ({
  impact,
  className,
  option,
  unit,
  hideIcon,
}) => {
  const t = useTranslations();

  const IMPACT_CONFIG = {
    [ImpactDirectionCategory.Increase]: {
      icon: <FontAwesomeIcon icon={faArrowUp} />,
      textKey: "increasesLikelihood",
      color: "text-olive-800 dark:text-olive-800-dark",
    },
    [ImpactDirectionCategory.Decrease]: {
      icon: <FontAwesomeIcon icon={faArrowDown} />,
      textKey: "decreasesLikelihood",
      color: "text-salmon-700 dark:text-salmon-700-dark",
    },
    [ImpactDirectionCategory.More]: {
      icon: <FontAwesomeIcon icon={faArrowUp} />,
      textKey: unit ? "more" : "increases",
      color: "text-olive-800 dark:text-olive-800-dark",
    },
    [ImpactDirectionCategory.Less]: {
      icon: <FontAwesomeIcon icon={faArrowDown} />,
      textKey: unit ? "less" : "decreases",
      color: "text-salmon-700 dark:text-salmon-700-dark",
    },
    [ImpactDirectionCategory.Earlier]: {
      icon: <FontAwesomeIcon icon={faArrowLeft} />,
      textKey: "hastens",
      color: "text-salmon-700 dark:text-salmon-700-dark",
    },
    [ImpactDirectionCategory.Later]: {
      icon: <FontAwesomeIcon icon={faArrowRight} />,
      textKey: "delays",
      color: "text-olive-800 dark:text-olive-800-dark",
    },
    [ImpactDirectionCategory.IncreaseUncertainty]: {
      icon: (
        <div className="flex gap-0.5 text-[8px]">
          <FontAwesomeIcon icon={faArrowLeft} />
          <FontAwesomeIcon icon={faArrowRight} />
        </div>
      ),
      textKey: "increasesUncertainty",
      color: "text-blue-700 dark:text-blue-700-dark",
    },
  } as const;

  const { icon, textKey, color } = IMPACT_CONFIG[impact];

  return (
    <div className={cn("flex items-center gap-1.5 text-xs", color, className)}>
      {!hideIcon && icon}
      <span>
        {t(textKey)}
        {impact !== ImpactDirectionCategory.IncreaseUncertainty && unit && (
          <>&nbsp;{unit}</>
        )}
        {option && (
          <>
            &nbsp;
            <RichText>
              {(tags) =>
                t.rich("forOption", {
                  ...tags,
                  option,
                })
              }
            </RichText>
          </>
        )}
      </span>
    </div>
  );
};
